import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Grid,
  Chip,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  MyLocation as MyLocationIcon,
  EmojiEvents as EmojiEventsIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../api';

const STORAGE_KEY = 'sportcruit_onboarded';

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'competitive', label: 'Competitive' },
];
const AGE_GROUPS = ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];

interface OnboardingDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Post-signup wizard: captures the profile data the matching engine needs
 * (location + player details) in the first minute, tailored by role.
 * Managers get routed to team creation instead of player questions.
 */
const OnboardingDialog: React.FC<OnboardingDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const isManager = user?.role === 'team_manager';

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [geoState, setGeoState] = useState<'idle' | 'locating' | 'done' | 'error'>('idle');
  const [form, setForm] = useState({
    city: '',
    state: '',
    latitude: null as number | null,
    longitude: null as number | null,
    ageGroup: '',
    skillLevel: '',
    position: '',
    availability: '',
  });

  // Managers: welcome → location → create team. Players/parents: + player details.
  const stepLabels = isManager
    ? ['Welcome', 'Location', 'Your team']
    : ['Welcome', 'Location', 'Player details'];
  const isLast = activeStep === stepLabels.length - 1;

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoState('error');
      return;
    }
    setGeoState('locating');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(prev => ({ ...prev, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        setGeoState('done');
      },
      () => setGeoState('error'),
    );
  };

  const finish = (destination?: string) => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
    if (destination) navigate(destination);
  };

  const handleNext = async () => {
    if (!isLast) {
      setActiveStep(s => s + 1);
      return;
    }
    // Final step: persist whatever was filled in, then route by role
    const payload: any = {};
    if (form.city) payload.city = form.city;
    if (form.state) payload.state = form.state;
    if (form.latitude != null) payload.latitude = form.latitude;
    if (form.longitude != null) payload.longitude = form.longitude;
    if (form.ageGroup) payload.ageGroup = form.ageGroup;
    if (form.skillLevel) payload.skillLevel = form.skillLevel;
    if (form.position) payload.position = form.position;
    if (form.availability) payload.availability = form.availability;

    if (Object.keys(payload).length > 0) {
      try {
        setSaving(true);
        const updated = await authAPI.updateProfile(payload);
        updateUser(updated);
      } catch {
        // Non-fatal — they can finish on the profile page
      } finally {
        setSaving(false);
      }
    }
    finish(isManager ? '/teams' : undefined);
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {isManager ? 'Let’s fill your roster' : 'Let’s find the right team'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mx: 2 }}>
            {isManager
              ? 'Two quick questions, then you can post open spots and tryouts that nearby players will actually see.'
              : 'Answer two quick questions and our matching engine will surface teams and tryouts near you that fit your player.'}
          </Typography>
        </Box>
      );
    }

    if (activeStep === 1) {
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Where are you based?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Distance is the biggest factor in a good match — this unlocks nearby teams and tryouts.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <TextField label="City" fullWidth size="small" value={form.city} onChange={set('city')} />
            </Grid>
            <Grid item xs={5}>
              <TextField label="State" fullWidth size="small" value={form.state} onChange={set('state')} inputProps={{ maxLength: 2 }} placeholder="TX" />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={geoState === 'done' ? <CheckCircleIcon /> : <MyLocationIcon />}
              onClick={handleUseMyLocation}
              disabled={geoState === 'locating'}
              color={geoState === 'done' ? 'success' : 'primary'}
            >
              {geoState === 'locating' ? 'Locating…' : geoState === 'done' ? 'Location captured' : 'Use my location'}
            </Button>
            {geoState === 'error' && (
              <Typography variant="caption" color="error">
                Couldn’t get your location — city and state work fine too.
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    if (isManager) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Create your team
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
            Set up your team page, then post open roster spots and public tryouts — players near you get matched and alerted automatically.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          About your player
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All optional — but each one sharpens your match scores.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField select label="Age group" fullWidth size="small" value={form.ageGroup} onChange={set('ageGroup')}>
              {AGE_GROUPS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Skill level" fullWidth size="small" value={form.skillLevel} onChange={set('skillLevel')}>
              {SKILL_LEVELS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label="Position" fullWidth size="small" value={form.position} onChange={set('position')} placeholder="e.g. Striker" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Availability" fullWidth size="small" value={form.availability} onChange={set('availability')} placeholder="e.g. Weekends" />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Chip
            label="Quick setup"
            size="small"
            sx={{ bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          />
        </Box>
        <Box sx={{ minHeight: 190 }}>{renderStep()}</Box>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 3, mb: 1 }}>
          {stepLabels.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button onClick={() => finish()} color="inherit" size="small">
          Skip for now
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(s => s - 1)} color="inherit">
              Back
            </Button>
          )}
          <Button variant="contained" onClick={handleNext} disabled={saving}>
            {saving ? 'Saving…' : isLast ? (isManager ? 'Create My Team' : 'See My Matches') : 'Next'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export { STORAGE_KEY as ONBOARDING_STORAGE_KEY };
export default OnboardingDialog;
